import { useExternalAccounts } from './api/use-get-external-accounts';
import { usePlaidItems } from '../plaid/api/use-get-plaid-items';
import { useMemo } from 'react';
import { useAccounts } from '../account/api/use-get-accounts';
/**
 * Converts external accounts into grouped select options organized by
 * “Institution Name (Provider)”. Groups and options are sorted alphabetically.
 */
export const getExternalAccountOptions = (externalAccounts) => {
    const optionGroups = externalAccounts.reduce((groups, externalAccount) => {
        const institutionName = externalAccount.institutionName || 'Unknown';
        const groupLabel = `${institutionName} (${externalAccount.provider})`;
        let existingGroup = groups.find((group) => group.label === groupLabel);
        const selectOption = {
            value: externalAccount.id,
            label: externalAccount.name,
            aside: externalAccount.balance.toString(),
        };
        if (existingGroup) {
            existingGroup.options.push(selectOption);
        }
        else {
            groups.push({ label: groupLabel, options: [selectOption] });
        }
        return groups;
    }, []);
    // Sort options within each group by label
    optionGroups.forEach((group) => {
        group.options.sort((a, b) => a.label.localeCompare(b.label));
    });
    // Sort groups by label
    return optionGroups.sort((a, b) => a.label.localeCompare(b.label));
};
/**
 * Hook to get External account IDs that have associated Plaid errors.
 *
 * This hook cross-references External accounts with Plaid items to identify
 * accounts that are experiencing errors. It matches accounts to Plaid items
 * using the providerGroupId (which contains the Plaid itemId).
 *
 * @returns {string[]} - Array of External account IDs that have Plaid errors.
 */
export const useExternalAccountsWithErrors = () => {
    const externalAccounts = useExternalAccounts();
    const plaidItems = usePlaidItems();
    return useMemo(() => {
        // create a set of itemIds that have errors for O(1) lookup
        const itemIdsWithErrors = new Set(plaidItems.filter((item) => item.errorCode !== null && item.errorCode !== undefined).map((item) => item.itemId));
        // filter external accounts that have a providerGroupId matching an errored itemId
        return externalAccounts
            .filter((account) => account.providerGroupId && itemIdsWithErrors.has(account.providerGroupId))
            .map((account) => account.id);
    }, [externalAccounts, plaidItems]);
};
/**
 * Hook to check if a specific account has a bank connection error.
 *
 * This hook delegates to useExternalAccountsWithErrors and checks if the
 * account's externalAccountId is in the list of accounts with errors.
 *
 * @param accountId - The account ID to check for bank errors
 * @returns True if the account has a bank connection error, false otherwise
 */
export const useAccountHasBankError = (accountId) => {
    const accounts = useAccounts();
    const externalAccountIdsWithErrors = useExternalAccountsWithErrors();
    return useMemo(() => {
        if (!accountId) {
            return false;
        }
        const account = accounts.find((acc) => acc.id === accountId);
        if (!account?.externalAccountId) {
            return false;
        }
        return externalAccountIdsWithErrors.includes(account.externalAccountId);
    }, [accountId, accounts, externalAccountIdsWithErrors]);
};
